/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useState } from 'react';
import ReactGA from 'react-ga';
import { Form, Button, Card, Icon } from 'antd';
import OtpInput from './components/OtpInput';
import { getLock, Lock, WrongSolutionType } from './lockConfigs';

type OnBackCallback = () => void;

type LockProps = {
  lock: number;
  onBack: OnBackCallback;
}

type SolvedFormProps = {
  lock: Lock;
  onBack: OnBackCallback;
};

const SolvedForm = ({ lock, onBack }: SolvedFormProps) => (
  <div className="solved">
    <Icon className="puzzleIcon" type="check-circle" theme="twoTone" twoToneColor="#52c41a" />
    <h1>Congratulations!</h1>
    <div className="solvedMessage">{lock?.getSolvedMessage()}</div>
    <Button type="primary" onClick={onBack}>
      Continue
    </Button>
  </div>
);

type LockFormProps = {
  lock: Lock;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  setAnswer: (answer: string) => void;
  answer: string;
}

const LockForm = ({ lock, onSubmit, answer, setAnswer }: LockFormProps) => (
  <>
    <Icon className="puzzleIcon" type="lock" theme="filled" />
    <div className="lockDescription">{lock && lock.getDescription()}</div>
    <Form onSubmit={onSubmit} className="lockForm">
      <Form.Item>
        <OtpInput
          containerStyle="solutionInputContainer"
          shouldAutoFocus={true}
          data-cy="solution-input"
          onChange={setAnswer}
          value={answer}
          numInputs={lock.config.solution.length}
          inputStyle="solutionInput"
          isInputNum={lock.config.solutionIsNumeric}
        />
      </Form.Item>
      <Button data-cy="solution-submit" type="primary" htmlType="submit">
        <Icon type="unlock" theme="filled" /> Unlock
      </Button>
    </Form>
  </>
);

type WrongSolutionWidgetProps = {
  wrongSolution: WrongSolutionType;
  resetWrongSolution: () => void;
};

const WrongSolutionWidget = ({ wrongSolution, resetWrongSolution }: WrongSolutionWidgetProps) => (
  <>
    <Icon className="puzzleIcon" type="question-circle" theme="twoTone" twoToneColor="#f58742" />
    <h1>Almost!</h1>
    <div className="solvedMessage">{wrongSolution.message}</div>
    <Button type="primary" onClick={resetWrongSolution}>
      Try again
    </Button>
  </>
);


type ContentWrapperProps = {
  onBack: OnBackCallback;
  children: React.ReactNode;
}

const ContentWrapper = ({ onBack, children }: ContentWrapperProps) => (
  <>
    <Card>
      <div className="backButtonWrapper">
        <a href="/#" onClick={onBack}><Icon type="arrow-left" /> BACK</a>
      </div>
      {children}
    </Card>
  </>
);

const LockPage = (props: LockProps) => {
  const lock = getLock(props.lock);

  const [solved, setSolved] = useState(false);
  const [answer, setAnswer] = useState('');
  const [wrongSolution, setWrongSolution] = useState(undefined as WrongSolutionType | undefined);

  if (!lock) {
    return (
      <ContentWrapper onBack={props.onBack}>
        <h1>Something went wrong, lock is missing... Please refresh the page and try again</h1>
      </ContentWrapper>
    );
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    if (lock.getWrongSolution(answer)) {
      ReactGA.event({
        category: 'Puzzle',
        action: `Set Wrong Solution (${lock.config.code})`,
        label: `${answer}`
      });
      return setWrongSolution(lock.getWrongSolution(answer));
    }

    if (lock.isSolutionCorrect(answer)) {
      ReactGA.event({
        category: 'Puzzle',
        action: `Set Solved (${lock.config.code})`,
        label: `${answer}`
      });
      return setSolved(true);
    }

    ReactGA.event({
      category: 'Puzzle',
      action: `Set Unknown Solution (${lock.config.code})`,
      label: `${answer}`
    });
    // eslint-disable-next-line no-alert
    alert(`${answer} is not a correct answer`);
  };

  if (wrongSolution) {
    return (
      <ContentWrapper onBack={props.onBack}>
        <WrongSolutionWidget
          wrongSolution={wrongSolution}
          resetWrongSolution={() => setWrongSolution(undefined)}
        />
      </ContentWrapper>
    );
  }

  if (solved) {
    return (
      <ContentWrapper onBack={props.onBack}>
        <SolvedForm
          lock={lock}
          onBack={props.onBack}
        />
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper onBack={props.onBack}>
      <LockForm
        answer={answer}
        setAnswer={setAnswer}
        lock={lock}
        onSubmit={onSubmit}
      />
    </ContentWrapper>
  );
};

export default LockPage;
